import { ProcessInformation, Timeline, TimelineItemState } from '@uag/react-core';
import { OnboardingRequestModel, StepModel, StepProgress, StepType } from '@uag/welcome-shared/src/api/models';
import { t } from 'i18next';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router';

import { StepAction } from './StepAction';
import { StepContact } from './StepContact';

type Props = {
    step: StepModel;
    request: OnboardingRequestModel;
};

const getProcessText = (step: StepModel) => {
    if (step.progress === StepProgress.InProgress) {
        return t('lastEditedOn');
    }
    if (step.progress === StepProgress.Completed) {
        return t('submittedOn');
    }
};

const getProcessInformation = (step: StepModel, request: OnboardingRequestModel) => {
    if (
        step.progress === StepProgress.Pending
        || step.type === StepType.VerificationResult
        || !step.user
        || !step.completedTimeStamp
        || (step.type === StepType.Verification && request.currentState === 'Declined')
    ) {
        return undefined;
    }

    return {
        name: step.user.displayName,
        email: step.user.email ?? undefined,
        image: step.user.profilePictureUri ?? undefined,
        processedDate: new Date(step.completedTimeStamp),
        processedText: getProcessText(step),
    } satisfies ProcessInformation;
};

const getState = (step: StepModel, request: OnboardingRequestModel) => {
    switch (step.progress) {
        case StepProgress.Completed: {
            if (step.type === StepType.Verification && request.currentState === 'Declined') {
                return TimelineItemState.Aborted;
            }
            return TimelineItemState.Done;
        }
        case StepProgress.InProgress:
        case StepProgress.Started:
            return TimelineItemState.Active;
        case StepProgress.Declined:
            return TimelineItemState.Aborted;
        default:
            return TimelineItemState.Pending;
    }
};

export const Step = ({ step, request }: Props) => {
    const { t } = useTranslation('translations', { keyPrefix: `steps.${step.type}.${step.progress}` });

    const processInformation = getProcessInformation(step, request);
    const state = getState(step, request);
    const title = t('title');
    const technicalAdministratorEmail = request.dokaOnboardingResponsible?.model.email;

    const getDescriptionComponents = (step: StepModel) => {
        const onlineShopAction = step.actions?.find((a) => a.actionType === 'OnlineShopLink');
        const myDokaAction = step.actions?.find((a) => a.actionType === 'MyDokaLink');

        const onlineShopLinkValue = onlineShopAction?.data?.link?.trim();
        const myDokaLinkValue = myDokaAction?.data?.link?.trim();

        const onlineShopLink = onlineShopLinkValue ? onlineShopLinkValue : 'https://shop.doka.com/';
        const myDokaLink = myDokaLinkValue ? myDokaLinkValue : 'https://mydoka.doka.com/';

        return {
            onlineShopLink: <Link className="link link-external inline" target="_blank" to={onlineShopLink} />,
            myDokaLink: <Link className="link link-external inline" target="_blank" to={myDokaLink} />,
        };
    };

    return (
        <Timeline.Item
            position={step.responsibleParty === 'Company' ? 'Right' : 'Left'}
            processInformation={step.responsibleParty === 'Company' ? processInformation : undefined}
            state={state}
            title={title}
        >
            <StepContact request={request} step={step} />
            <span>
                <Trans
                    components={getDescriptionComponents(step)}
                    i18nKey="description"
                    t={t}
                    values={{ email: technicalAdministratorEmail }}
                />
            </span>
            <div className="gap-base flex flex-row">
                {step.actions
                    && step.actions.map(
                        (stepAction, index) =>
                            stepAction.actionType !== 'OnlineShopLink'
                            && stepAction.actionType !== 'MyDokaLink' && (
                                <StepAction key={index} stepAction={stepAction} stepProgress={step.progress} />
                            ),
                    )}
            </div>
        </Timeline.Item>
    );
};
